<script>
  import { getFolderPermissions } from "./permissions";
  import { clickOutside } from "@ui/clickOutside";
  import { fade } from "svelte/transition";
  import { getJson, postJson, ServerError } from "@ui/http";
  import { getFoldersInfo } from "./folders";
  import { getCurrentUser } from "@ui/currentUser";
  import ErrorModal from "@ui/ErrorModal.svelte";
  import { getConfirmationStore } from "@ui/confirmationStore";

  const errorCodesToText = {};

  export let newsletter;
  const currentUser = getCurrentUser();
  const permissions = getFolderPermissions();
  const folderId = permissions.folderId;
  const foldersInfo = getFoldersInfo();
  const confirmationStore = getConfirmationStore();
  let showOptions = false;
  let showAddToFolder = false;
  let errorMsg = undefined;
  let errorTitle = undefined;
  let postInAction = undefined;

  $: $currentUser.caps.enhance_debugging_duplicate_newsletter
    ? console.log("Overlay: showOptions", { showOptions, id: newsletter.id })
    : {};

  function duplicateClicked() {
    if ($currentUser.caps.enhance_debugging_duplicate_newsletter) {
      console.log("duplicateClicked: BEFORE confirmationStore.showConfirmation", $confirmationStore);
    }
    if ($currentUser.caps.disable_confirmation_in_newsletter_card_overlay) {
      console.log("disable_confirmation_in_newsletter_card_overlay flag is ON, skipping `duplicate` confirmation");
      return doDuplicate();
    }

    confirmationStore.showConfirmation(
      "Duplicate newsletter",
      "Create a copy of this newsletter and edit the copy?",
      "Duplicate",
      doDuplicate
    );

    if ($currentUser.caps.enhance_debugging_duplicate_newsletter) {
      console.log("duplicateClicked: AFTER confirmationStore.showConfirmation", $confirmationStore);
    }
  }

  async function doDuplicate() {
    try {
      postInAction = "Duplicating...";
      const data = {
        id: newsletter.id
      };
      $confirmationStore.confirmButtonText = "Duplicating...";
      confirmationStore.disable();
      const editNewsletterUrl = await postJson("/app/pages/duplicate_json", data);
      document.location.href = editNewsletterUrl;
    } catch (err) {
      if (err.code === "no_credits") {
        // setTimeout is needed in order to allow the previous confirmation process to complete and show the new message
        // Otherwise the confirmation will not be shown
        setTimeout(() => {
          if ($currentUser.isOrgArchived) {
            confirmationStore.showConfirmation(
              "You're out of newsletter credits 😢",
              `Your Account has been Archived 😔
              If you’d like to create more newsletters, contact Smore below.`,
              "Contact Smore",
              doContactSmore
            );
          } else {
            confirmationStore.showConfirmation(
              "You're out of newsletter credits 😢",
              "You are currently on a Free Trial, and can’t create or duplicate any more newsletters. Upgrade now and get more newsletters, enhanced analytics, education backgrounds and more!",
              "Upgrade",
              doUpgrade
            );
          }
        }, 30);
      } else {
        errorTitle = "Error duplicating newsletter";
        errorMsg = errorCodesToText[err.code] || ServerError.GENERIC_ERROR;
      }
    } finally {
      confirmationStore.enable();
      postInAction = undefined;
    }
  }

  function doUpgrade() {
    window.open("/pricing?ur=free_user_nl_credits", "_blank");
  }

  function doContactSmore() {
    window.open("/contact-renewal?t=archived&s=duplicate", "_blank");
  }

  async function deleteClicked() {
    if ($currentUser.caps.disable_confirmation_in_newsletter_card_overlay) {
      console.log("disable_confirmation_in_newsletter_card_overlay flag is ON, skipping `delete` confirmation");
      return doDelete();
    }
    confirmationStore.showConfirmation(
      "Delete newsletter",
      "Checking to see if this newsletter has any Scheduled Deliveries planned....",
      "Delete",
      console.log
    );
    confirmationStore.disable();

    const numberOfScheduledDeliveries = await getJson(`/app/newsletters/check_scheduled_deliveries/${newsletter.id}`);
    let message = "Are you sure you want to delete this newsletter?";
    if (numberOfScheduledDeliveries === 1) {
      message = `<i>Uh oh!</i> This newsletter has future plans.<br/><br/>Are you sure you want to delete this newsletter and cancel all of its scheduled deliveries?`;
    }
    confirmationStore.showConfirmation("Delete newsletter", message, "Delete", doDelete);
  }

  async function doDelete() {
    try {
      postInAction = "Deleting...";
      await postJson(`/app/pages/delete_json`, {
        id: newsletter.id
      });
      document.location.reload();
    } catch (err) {
      errorTitle = "Error deleting newsletter";
      if (err.code && err.code.startsWith("concurrent_edit:")) {
        const editingUserName = err.code.replace("concurrent_edit:", "");
        errorMsg = `Cannot delete newsletter. ${editingUserName} is currently working on it.`;
      } else {
        errorMsg = errorCodesToText[err.code] || ServerError.GENERIC_ERROR;
      }
    } finally {
      postInAction = undefined;
    }
  }

  async function removeClicked() {
    if ($currentUser.caps.disable_confirmation_in_newsletter_card_overlay) {
      console.log("disable_confirmation_in_newsletter_card_overlay flag is ON, skipping `remove` confirmation");
      return doRemove();
    }
    confirmationStore.showConfirmation(
      "Remove newsletter from folder",
      "Remove this newsletter from the current folder?",
      "Remove",
      doRemove
    );
  }

  async function doRemove() {
    try {
      postInAction = "Removing...";
      await postJson(`/app/page_folders/remove_page_id_from_folder_json`, {
        page_id: newsletter.id,
        folder_id: folderId
      });
      document.location.reload();
    } catch (err) {
      errorTitle = "Error removing newsletter";
      errorMsg = errorCodesToText[err.code] || ServerError.GENERIC_ERROR;
    } finally {
      postInAction = undefined;
    }
  }

  async function privacyClicked() {
    if ($currentUser.caps.disable_confirmation_in_newsletter_card_overlay) {
      console.log("disable_confirmation_in_newsletter_card_overlay flag is ON, skipping `privacy` confirmation");
      return doChangePrivacy();
    }
    confirmationStore.showConfirmation(
      "Change newsletter privacy",
      newsletter.privacy === "public" ? "Make this newsletter private?" : "Make this newsletter public?",
      "Change",
      doChangePrivacy
    );
  }

  async function doChangePrivacy() {
    try {
      const newPrivacy = newsletter.isPublic ? "unlisted" : "public";
      postInAction = "Updating privacy...";
      await postJson(`/app/pages/change_privacy_json`, {
        id: newsletter.id,
        privacy: newPrivacy
      });
      newsletter.isPublic = newsletter.isPublic;
      document.location.reload();
    } catch (err) {
      errorTitle = "Error changing privacy";
      errorMsg = errorCodesToText[err.code] || ServerError.GENERIC_ERROR;
    } finally {
      postInAction = undefined;
    }
  }

  function addToFolderClicked() {
    foldersInfo.showAddToFolder(newsletter.id);
  }

  function restoreToFolderClicked() {
    foldersInfo.showRestoreToFolder(newsletter.id);
  }
</script>

<ErrorModal bind:errorMsg title={errorTitle} />

<div
  class="absolute top-0 left-0 right-0 flex items-center justify-between p-3 text-sm text-white"
  use:clickOutside={() => {
    if (!showAddToFolder) {
      showOptions = false;
    }
  }}>
  {#if permissions.can_edit_newsletters}
    <a
      class="flex items-center px-2 py-px text-sm transition-colors border border-transparent rounded bg-brand-gray-80 hover:bg-brand-gray sm-clickbale justift-start"
      href={newsletter.urls.edit}>
      <svg class="mr-1 -mt-px" width="11" height="10" viewBox="0 0 11 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M10.8324 2.384L10.07 3.152L7.84626 0.912L8.54515 0.208C8.54515 0.208 8.54515 0.144 8.60868 0.144C8.79929 -0.048 9.11696 -0.048
          9.3711 0.144L10.8959 1.68C11.023 1.808 11.023 2.192 10.8324 2.384ZM8.98989 3.408L9.3711 3.792L4.35185 8.848L2.12812 6.608L7.14738
          1.552L7.52859 1.872L8.98989 3.408ZM7.9098 2.256L8.60868 3.024L7.9098 2.256ZM3.58943 9.616L2.25519 9.808L0.984497 10L1.1751
          8.656L1.36571 7.312L2.50933 8.464L3.58943 9.616Z"
          fill="white" />
      </svg>
      Edit
    </a>
  {:else}
    <div />
  {/if}
  <div
    class="z-20 px-2 py-px text-sm transition-colors border border-transparent rounded sm-clickable bg-brand-gray-80 hover:bg-brand-gray"
    on:click|preventDefault|stopPropagation={() => (showOptions = !showOptions)}
    class:border-nxgray-200={showOptions}>
    Options
  </div>
</div>
{#if postInAction}
  <div
    on:click|preventDefault|stopPropagation
    class="absolute inset-0 z-50 flex items-center justify-center px-3 bg-opacity-75 rounded cursor-default bg-nxgray-500">
    <div class="px-3 py-1 font-medium text-gray-100 border-2 border-gray-200 animate-pulse text-13 rounded-2xl">
      {postInAction}
    </div>
  </div>
{/if}
{#if showOptions}
  <div
    on:click|stopPropagation|preventDefault
    transition:fade={{ duration: 200 }}
    class="absolute inset-0 z-20 flex flex-col items-center justify-center p-4 rounded cursor-default"
    style="background:rgba(0,0,0,.7)"
    on:mouseleave|preventDefault|stopPropagation={() => {
      if (!showAddToFolder) {
        showOptions = false;
      }
    }}
    on:click|preventDefault|stopPropagation>
    {#if newsletter.isDeleted}
      <div
        class="w-full p-1 text-sm text-center text-white transition-colors bg-black border-2 border-gray-400 rounded-full shadow sm-clickable hover:bg-green-600 hover:border-white"
        on:click={restoreToFolderClicked}>
        Restore
      </div>
    {:else}
      <div
        class="w-full p-1 text-sm text-center text-white transition-colors bg-black border-2 border-gray-400 rounded-full shadow sm-clickable hover:bg-green-600 hover:border-white"
        on:click|preventDefault|stopPropagation={duplicateClicked}>
        Duplicate
      </div>
    {/if}

    {#if !newsletter.isDraft && !newsletter.isDeleted}
      <div
        class="w-full p-1 mt-3 text-sm text-center text-white transition-colors bg-black border-2 border-gray-400 rounded-full shadow sm-clickable hover:bg-green-600 hover:border-white"
        on:click={privacyClicked}>
        {#if newsletter.isPublic}Make private{:else}Make public{/if}
      </div>
    {/if}

    {#if permissions.can_remove_newsletters}
      <div
        class="w-full p-1 mt-3 text-sm text-center text-white transition-colors bg-black border-2 border-gray-400 rounded-full shadow sm-clickable hover:bg-red-600 hover:border-white"
        on:click={removeClicked}>
        Remove from folder
      </div>
    {/if}

    {#if foldersInfo && (folderId === "my" || folderId === "drafts")}
      <div
        class="w-full p-1 mt-3 text-sm text-center text-white transition-colors bg-black border-2 border-gray-400 rounded-full shadow sm-clickable hover:bg-green-600 hover:border-white"
        on:click={addToFolderClicked}>
        Add to folder
      </div>
    {/if}

    {#if permissions.can_delete_newsletters}
      <div
        class="w-full p-1 mt-3 text-sm text-center text-white transition-colors bg-black border-2 border-gray-400 rounded-full shadow sm-clickable hover:bg-red-600 hover:border-white"
        on:click={deleteClicked}>
        Delete
      </div>
    {/if}
  </div>
{/if}
